import { clsx } from 'clsx';

interface GridLinesProps {
  className?: string;
}

// Base color for grid lines (border-primary: #e4e4e7)
const GRID_COLOR = '228, 228, 231';

/**
 * Visual overlay showing grid columns and horizontal lines with dynamic opacity.
 *
 * Opacity is controlled via CSS variables:
 * - --grid-external-opacity: opacity for first/last column lines
 * - --grid-internal-opacity: opacity for internal column lines
 *
 * Desktop (≥1280px): 12 columns
 * Tablet (768-1279px): 8 columns
 * Mobile (<768px): 4 columns
 */
export function GridLines({ className }: GridLinesProps) {
  return (
    <div
      className={clsx(
        'fixed inset-0 pointer-events-none z-0',
        // Smooth transition when intensity changes
        '[&_*]:transition-[border-color] [&_*]:duration-500 [&_*]:ease-out',
        className
      )}
      aria-hidden="true"
    >
      <div
        className={clsx(
          'h-full max-w-[1440px] mx-auto',
          'grid grid-cols-4 px-6', // Mobile: 4 columns, 24px padding
          'md:grid-cols-8 md:px-16', // Tablet: 8 columns, 64px padding
          'xl:grid-cols-12 xl:px-[180px]' // Desktop: 12 columns, 180px padding
        )}
      >
        {Array.from({ length: 12 }).map((_, index) => {
          const isExternal = index === 0;
          const isLastMobile = index === 3;
          const isLastTablet = index === 7;
          const isLastDesktop = index === 11;

          return (
            <div
              key={index}
              className={clsx(
                'h-full border-l [border-left-style:dashed]',
                // Column 4-7: hidden on mobile, visible on tablet+
                index >= 4 && index < 8 && 'hidden md:block',
                // Column 8-11: hidden on mobile/tablet, visible on desktop
                index >= 8 && 'hidden xl:block',
                // Right borders for last visible columns at each breakpoint
                isLastMobile && 'border-r [border-right-style:dashed] md:border-r-0',
                isLastTablet && 'md:border-r md:[border-right-style:dashed] xl:border-r-0',
                isLastDesktop && 'xl:border-r xl:[border-right-style:dashed]'
              )}
              style={{
                // Left border: external (first) or internal (rest)
                borderLeftColor: isExternal
                  ? `rgba(${GRID_COLOR}, var(--grid-external-opacity, 0.50))`
                  : `rgba(${GRID_COLOR}, var(--grid-internal-opacity, 0.05))`,
                // Right borders for last columns (always external opacity)
                ...(isLastMobile || isLastTablet || isLastDesktop
                  ? { borderRightColor: `rgba(${GRID_COLOR}, var(--grid-external-opacity, 0.50))` }
                  : {}),
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
